<template>
  <div class="mcd-wrapper" ref="mcd">
    <button class="mcd-trigger" type="button" @click.stop="toggle">
      <span class="mcd-label" :title="selectedLabel">{{ selectedLabel }}</span>
      <svg class="mcd-chevron" :class="{ 'is-open': isOpen }" xmlns="http://www.w3.org/2000/svg"
           width="13" height="13" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="6 9 12 15 18 9"/>
      </svg>
    </button>

    <transition name="mcd-drop">
      <ul v-if="isOpen" class="mcd-dropdown">
        <li
          v-for="opt in options"
          :key="opt.originalIndex"
          class="mcd-option"
          :class="{ 'is-active': opt.originalIndex === value, 'is-disabled': opt.disabled }"
          @click="!opt.disabled && select(opt)"
        >
          <svg v-if="opt.originalIndex === value"
               class="mcd-check" xmlns="http://www.w3.org/2000/svg"
               width="12" height="12" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
          <span v-else class="mcd-check-placeholder"></span>
          <span class="mcd-option-label">{{ opt.marketAlias || opt.marketName }}</span>
        </li>
      </ul>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'MarketColumnDropdown',
  props: {
    value:   { type: Number, default: 0 },
    options: { type: Array,  default: () => [] },
  },
  data() {
    return { isOpen: false };
  },
  computed: {
    selectedLabel() {
      const opt = this.options.find(o => o.originalIndex === this.value);
      return opt ? (opt.marketAlias || opt.marketName) : '—';
    },
  },
  methods: {
    toggle() {
      this.isOpen = !this.isOpen;
    },
    select(opt) {
      this.$emit('change', opt.originalIndex);
      this.isOpen = false;
    },
    onClickOutside(e) {
      if (this.$refs.mcd && !this.$refs.mcd.contains(e.target)) {
        this.isOpen = false;
      }
    },
  },
  mounted() {
    document.addEventListener('click', this.onClickOutside, true);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onClickOutside, true);
  },
};
</script>

<style scoped>
/* ── Component-scoped tokens ─────────────────────────────────────────────── */
/* Override any of these on a parent selector to restyle only this dropdown  */
.mcd-wrapper {

  position: relative;
  width: 100%;
  height: 34px;
}

.mcd-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  padding: 0 9px 0 12px;
  background: var(--mcd-trigger-bg);
  border: 1px solid var(--mcd-trigger-border);
  border-radius: var(--mcd-trigger-radius);
  color: var(--mcd-trigger-text);
  font-size: var(--font-size-xl);
  font-weight: 600;
  letter-spacing: 0.2px;
  cursor: pointer;
  gap: 6px;
  text-align: left;
  transition: border-color 0.18s ease, background-color 0.18s ease, box-shadow 0.18s ease;
}

.mcd-trigger:hover,
.mcd-trigger:focus-visible {
  background: var(--mcd-trigger-bg-hover);
  border-color: var(--mcd-trigger-border-hover);
  box-shadow: 0 0 0 2px var(--mcd-trigger-shadow-hover);
  outline: none;
}

.mcd-label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  display: flex;
  justify-content: center;
}

.mcd-chevron {
  flex-shrink: 0;
  color: var(--mcd-chevron-color);
  transition: transform 0.18s ease, color 0.18s ease;
}

.mcd-chevron.is-open {
  transform: rotate(180deg);
  color: var(--mcd-chevron-color-open);
}

.mcd-trigger:hover .mcd-chevron {
  color: var(--mcd-chevron-color-open);
}

/* ── Dropdown panel ──────────────────────────────────────────────────────── */
.mcd-dropdown {
  position: absolute;
  top: calc(100% + 5px);
  left: 0;
  right: 0;
  z-index: 300;
  background: var(--mcd-panel-bg);
  border: 1px solid var(--mcd-panel-border);
  border-radius: var(--mcd-panel-radius);
  box-shadow: var(--mcd-panel-shadow);
  padding: 4px;
  list-style: none;
  margin: 0;
  min-width: var(--mcd-panel-min-width);
}

/* ── Option rows ─────────────────────────────────────────────────────────── */
.mcd-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: var(--mcd-option-radius);
  font-size: var(--font-size-2xl);
  font-weight: 500;
  color: var(--mcd-option-text);
  cursor: pointer;
  transition: background-color 0.12s ease;
  user-select: none;
}

.mcd-option:hover:not(.is-disabled) {
  background: var(--mcd-option-bg-hover);
}

.mcd-option.is-active {
  color: var(--mcd-option-text-active);
  font-weight: 700;
}

.mcd-option.is-disabled {
  color: var(--mcd-option-text-disabled);
  cursor: default;
}

.mcd-check {
  flex-shrink: 0;
  color: var(--mcd-check-color);
}

.mcd-check-placeholder {
  flex-shrink: 0;
  width: 12px;
  height: 12px;
  display: inline-block;
}

.mcd-option-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ── Open/close animation ────────────────────────────────────────────────── */
.mcd-drop-enter-active,
.mcd-drop-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.mcd-drop-enter,
.mcd-drop-leave-to {
  opacity: 0;
  transform: translateY(-6px) scale(0.98);
}
</style>
